import type { Locale } from '@tuic/i18n';
import type { Trip } from '../../generated/pcl';
import { CacheTag } from '../../utils';
import { slugify } from '@tuic/lib/src/helpers/slug';
import { remember, serializeDataToCacheKey } from '@tuic/cache';
import { Pcl } from '../../index';
import { logger } from '@tuic/logger';

export type ApiKitTrip = Omit<Trip, 'code'> & {
  code: string;
  slug: string | undefined;
};

/**
 * Fetches the trip details for given tripCode.
 */
export const getTripDetails = async (
  tripCode: string,
  locale: Locale,
): Promise<ApiKitTrip | null> => {
  let { data, error } = await Pcl.getTrips({
    query: { locale, tripCodes: tripCode },
  });

  if (error) {
    logger.error(
      `Could not fetch trip details. Trip: ${tripCode}, Locale: ${locale}`,
    );
    logger.error(error);

    return null;
  }

  if (typeof data === 'undefined' || data.length <= 0) {
    return null;
  }

  let trip = data[0];

  return {
    ...trip,
    code: trip.code ?? tripCode,
    slug: trip.name ? slugify(trip.name) : undefined,
  };
};

/**
 * Fetches the trip details for given tripCode.
 */
export const cachedGetTripDetails = (
  tripCode: string,
  locale: Locale,
): Promise<ApiKitTrip | null> => {
  return remember(
    serializeDataToCacheKey([CacheTag.pcl, 'getTripDetails', locale, tripCode]),
    () => getTripDetails(tripCode, locale),
    {
      tags: [CacheTag.pcl, CacheTag.tripResource, CacheTag.getTrips],
      ttl: 600,
    },
  );
};

/**
 * Fetches the trip details for all given tripCodes.
 */
export const getMultipleTripsDetails = async (
  tripCodes: string[],
  locale: Locale,
): Promise<ApiKitTrip[]> => {
  let { data, error } = await Pcl.getTrips({
    query: { locale, tripCodes: tripCodes.join(',') },
  });

  if (error) {
    let tripCodesList = tripCodes.join(', ');
    logger.error(
      `Could not fetch multiple trip details. Trips: ${tripCodesList}, Locale: ${locale}`,
    );
    logger.error(error);

    return [];
  }

  return (data ?? [])
    .map(trip => ({
      ...trip,
      slug: trip.name ? slugify(trip.name) : undefined,
    }))
    .filter((trip): trip is ApiKitTrip => {
      return typeof trip.code === 'string';
    });
};

export const cachedGetMultipleTripsDetails = async (
  tripCodes: string[],
  locale: Locale,
): Promise<ApiKitTrip[]> => {
  if (tripCodes.length === 1) {
    let trip = await cachedGetTripDetails(tripCodes[0], locale);

    return trip ? [trip] : [];
  }

  let key = serializeDataToCacheKey([
    CacheTag.pcl,
    'getMultipleTripsDetails',
    locale,
    tripCodes,
  ]);

  const options = {
    tags: [
      CacheTag.pcl,
      CacheTag.tripResource,
      CacheTag.getMultipleTripsDetails,
    ],
    ttl: 600,
  };

  return remember(
    key,
    () => {
      return getMultipleTripsDetails(tripCodes, locale);
    },
    options,
  );
};
